<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import TheNavbar from './the-navbar.vue';
import TheAuditNavbar from './the-audit-navbar.vue';
import AuthorIdentityDialog from './users/author-identity-dialog.vue';
import type { RailsPageData, RouteComponentMap, AppState, RouteError } from '../types/rails-integration';

const state = ref<AppState>({
  isLoading: true,
  currentComponent: '',
  componentProps: {},
  error: null,
});

const routeToComponentMap: RouteComponentMap = {
  // Dashboard
  'dashboard/index': 'DashboardView',

  // Shifts
  'today_shifts/index': 'ShiftsTodayPage',
  'shifts/index': 'ShiftsPage',
  'shifts/show': 'ShiftShow',
  'shifts/new': 'ShiftBatchCreateForm',
  'shifts/edit': 'ShiftBatchCreateForm',
  'shift_imports/new': 'ShiftImportForm',
  'shift_imports/show': 'ShiftImportDetail',

  // Caregivers
  'caregivers/index': 'CaregiversList',
  'caregivers/show': 'CaregiverProfile',
  'caregivers/new': 'CaregiverForm',
  'caregivers/edit': 'CaregiverForm',
  'caregivers/passwords/new': 'RecoverPasswordForm',
  'caregivers/passwords/edit': 'EditPasswordForm',

  // Patients
  'patients/index': 'PatientsList',
  'patients/show': 'PatientProfile',
  'patients/new': 'PatientForm',
  'patients/edit': 'PatientForm',
  'patient_shifts/index': 'PatientShifts',
  'patient_vital_signs/index': 'PatientVitalSigns',

  // Calendar
  'calendar/index': 'CalendarView',

  // Charges & Payments
  'charges/index': 'ChargesList',
  'charge_closures/index': 'ChargeClosuresList',
  'payments/index': 'PaymentsList',

  // Route Plans
  'route_plans/index': 'RoutePlansList',
  'route_plans/show': 'RoutePlanDetail',

  // Monthly Tracking
  'monthly_tracking/index': 'MonthlyTrackingPage',
  'coverage_alerts/index': 'CoverageAlertsPage',

  // Planning (control panel)
  'planning/index': 'PlanningPage',

  // Settings
  'settings/index': 'SettingsView',

  // Inventory
  'inventory/index': 'InventoryView',
  'erp_integration/show': 'ErpIntegrationView',

  // Health Statuses
  'health_statuses/index': 'HealthStatusesView',

  // Representatives
  'representatives/passwords/new': 'RecoverPasswordForm',
  'representatives/passwords/edit': 'EditPasswordForm',

  // User Management
  'users/passwords/new': 'UserRecoverPasswordForm',
  'users/passwords/edit': 'UserEditPasswordForm',
  'users/sessions/new': 'UserLoginForm',

  // Audit Routes
  'audit_users/sessions/new': 'AuditUserLoginForm',
  'audit_users/caregiver_sessions/new': 'AuditUserCaregiverLoginForm',
  'audit_users/user_sessions/new': 'AuditUserUserLoginForm',
  'audit_users/service_company_sessions/new': 'AuditUserServiceCompanyLoginForm',
  'audit_users/passwords/new': 'AuditUserRecoverPasswordForm',
  'audit/portal/index': 'CompaniesList',
  'audit/companies/show': 'CompanyShow',
  'audit/login/index': 'AuditLoginView',
};

const isAuditApp = computed(() => {
  const pageData = (window as unknown as { railsPageData: RailsPageData }).railsPageData;

  return pageData?.isAudit || false;
});

const navbarComponent = computed(() => (isAuditApp.value ? TheAuditNavbar : TheNavbar));

function handleReload() {
  window.location.reload();
}

function getPropType(propName: string): 'binded' | 'vue' | 'normal' {
  if (propName.startsWith(':') || propName.startsWith('v-bind:')) return 'binded';
  else if (propName.startsWith('v-')) return 'vue';

  return 'normal';
}

function getComponentElement(htmlContent: string, componentName: string): Element | null {
  const temporalDiv = document.createElement('div');
  temporalDiv.innerHTML = htmlContent;

  const kebabName = componentName.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '');
  const selectors = [kebabName, componentName.toLowerCase(), componentName];

  let componentElement: Element | null = null;
  for (const selector of selectors) {
    componentElement = temporalDiv.querySelector(selector);
    if (componentElement) break;
  }

  return componentElement;
}

// eslint-disable-next-line max-statements, complexity
function getBindedPropValue(propValue: string): unknown {
  try {
    const cleanValue = propValue.trim();

    if (cleanValue.startsWith('{') || cleanValue.startsWith('[')) {
      try {
        return JSON.parse(cleanValue);
      } catch {
        let jsonValue = cleanValue.replace(/'/g, '"');
        jsonValue = jsonValue.replace(/(\w+):/g, '"$1":');

        return JSON.parse(jsonValue);
      }
    } else if (cleanValue === 'true' || cleanValue === 'false') {
      return cleanValue === 'true';
    } else if (cleanValue === 'null') {
      return null;
    } else if (cleanValue === 'undefined') {
      return undefined;
    } else if (!isNaN(Number(cleanValue)) && cleanValue !== '') {
      return Number(cleanValue);
    } else {
      try {
        return JSON.parse(cleanValue);
      } catch {
        return cleanValue;
      }
    }
  } catch {
    return propValue;
  }
}

function getNormalPropValue(propValue: string): unknown {
  if (propValue === 'true') return true;
  else if (propValue === 'false') return false;
  else if (propValue === 'null') return null;
  else if (!isNaN(Number(propValue)) && propValue !== '') return Number(propValue);

  return propValue;
}

function extractPropsFromComponentElement(props: Record<string, unknown>, componentElement: Element): void {
  Array.from(componentElement.attributes).forEach(attr => {
    let propName = attr.name;
    let propValue: unknown = attr.value;

    propName = propName.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
    const propType = getPropType(propName);

    if (propType === 'binded') {
      propName = propName.replace(/^:/, '').replace(/^v-bind:/, '');
      propValue = getBindedPropValue(propValue as string);
    } else if (propType === 'normal') {
      propValue = getNormalPropValue(propValue as string);
    } else {
      return;
    }

    props[propName] = propValue;
  });

  const dataElements = componentElement.querySelectorAll('[data-prop]');
  dataElements.forEach(element => {
    const propName = element.getAttribute('data-prop');
    const propValue = element.textContent || element.innerHTML;

    if (propName) {
      try {
        props[propName] = JSON.parse(propValue.trim());
      } catch {
        props[propName] = propValue.trim();
      }
    }
  });
}

function extractVuePropsFromRailsHTML(htmlContent: string, componentName: string): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  const componentElement = getComponentElement(htmlContent, componentName);

  if (componentElement) {
    extractPropsFromComponentElement(props, componentElement);
  }

  return props;
}

function getComponentName(): string {
  const pageData = (window as unknown as { railsPageData: RailsPageData }).railsPageData;
  if (!pageData) throw new Error('Rails page data not found');

  const componentName = routeToComponentMap[pageData.currentRoute];
  if (!componentName) {
    const error: RouteError = {
      type: 'ROUTE_NOT_MAPPED',
      message: `Route "${pageData.currentRoute}" is not mapped to a Vue component`,
      route: pageData.currentRoute,
    };

    state.value.error = error.message;
    state.value.isLoading = false;
    throw new Error(error.message);
  }

  return componentName;
}

function setPageError(error: RouteError) {
  state.value.error = error instanceof Error ? error.message : 'Unknown initialization error';
}

function initializeApp() {
  try {
    const componentName = getComponentName();
    const originalContent = document.querySelector('#vue-app-content')?.innerHTML || '';
    const extractedProps = originalContent ? extractVuePropsFromRailsHTML(originalContent, componentName) : {};

    state.value.currentComponent = componentName;
    state.value.componentProps = extractedProps;
  } catch (error) {
    setPageError(error as RouteError);
  } finally {
    state.value.isLoading = false;
  }
}

onMounted(initializeApp);
</script>
<template>
  <v-app>
    <component :is="navbarComponent" />
    <author-identity-dialog v-if="!isAuditApp" />

    <v-main class="justify-center mt-4 mt-md-8 mr-2 mr-md-12 ml-2 ml-md-4 d-flex align-start">
      <div
        v-if="state.isLoading"
        class="py-8 text-center"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        />
        <p class="mt-4 text-h6">
          Loading application...
        </p>
      </div>
      <div
        v-else-if="state.error"
        class="py-8 text-center"
      >
        <v-icon
          icon="mdi-alert-circle"
          size="64"
          color="error"
          class="mb-4"
        />
        <h2 class="mb-2 text-h5">
          Route Not Found
        </h2>
        <p class="mb-4 text-body-1">
          {{ state.error }}
        </p>
        <v-btn
          color="primary"
          variant="outlined"
          @click="handleReload"
        >
          <v-icon
            icon="mdi-refresh"
            start
          />
          Refresh Page
        </v-btn>
      </div>

      <component
        :is="state.currentComponent"
        v-else-if="state.currentComponent"
        v-bind="state.componentProps"
        @error="(error: string) => state.error = error"
      />

      <div
        v-else
        class="py-8 text-center"
      >
        <v-icon
          icon="mdi-help-circle"
          size="64"
          color="warning"
          class="mb-4"
        />
        <h2 class="mb-2 text-h5">
          No Component Found
        </h2>
        <p class="text-body-1">
          Unable to determine which component to render.
        </p>
      </div>
    </v-main>
  </v-app>
</template>
<style scoped>
.v-main {
  min-height: 100vh;
}
</style>
